import React, { useEffect } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { shouldShowCreateRule, getUser, shouldShowExecutionOrder } from 'Selectors';
import { useTranslation } from 'react-i18next';
import { FormikValues } from 'formik';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { addAdditionalBreadcrumbParams } from 'Store';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useGlobalRules from 'Hooks/query-hooks/global-rules/useGlobalRules';
import useMerchantRules from 'Hooks/query-hooks/single-merchant/useMerchantRules';
import useUpdateMerchantOverview from 'Hooks/query-hooks/single-merchant/useUpdateMerchantOverview';
import { useQueryParam } from 'Hooks/useQueryParam';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import useGetList from 'Hooks/query-hooks/list/useGetList';
import useGetTenantGroupInformation from 'Hooks/query-hooks/tenant-groups/useGetTenantGroupInformation';
import useDeployedLandlordRules from 'Hooks/query-hooks/useDeployedLandlordRules';
import { useGetTenantGroupOverridesInherited } from 'Hooks/query-hooks/tenant-groups/useGetTenantGroupOverrides';
import useGetConclusionFlags from 'Hooks/query-hooks/useGetConclusionFlags';
import useGetTenantGroup from 'Hooks/query-hooks/tenant-groups/useGetTenantGroup';
import useGetListNames from 'Hooks/query-hooks/list/useGetListNames';

import RouteLoading from 'Components/RouteLoading';
import EditSimpleRuleForm from 'Components/EditRulePage/EditSimpleRuleForm';
import EditAdvancedRuleForm from 'Components/EditRulePage/EditAdvancedRuleForm';
import WarningModalContent from 'Components/ConcurrencyHelpers/WarningModalContent';
import errorController from 'Components/ErrorScreens';

type Props = {
    actionType: OverrideAction;
    overrideLevel: OverrideLevel;
};

const AddOrEditMerchantOverride = ({ actionType, overrideLevel }: Props) => {
    const { ruleId, mid, tenantGroupId: tenantGroupIdString } = useParams<{
        ruleId: string;
        mid: string;
        tenantGroupId: string;
    }>();
    const { data: conclusionFlagListData } = useGetConclusionFlags();
    const { tenantKey, landlordId } = useSelector(getUser);
    // the current user may not belong to a tenant group
    // query the tenant group information about the current user
    // if no data returned, there's no tenant group
    const { data: tenantGroupInformation } = useGetTenantGroupInformation(tenantKey);
    const needTenantGroupData =
        (overrideLevel === 'tenant' && !!tenantGroupInformation) || overrideLevel === 'tenantGroup';
    const resolvedTenantGroupIdString = tenantGroupIdString || tenantGroupInformation?.tenantGroupId?.toString() || '';
    const {
        parsed: { selectedRowId }
    } = useQueryParam();
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const isCreateRuleAllowed = shouldShowCreateRule();
    const { addTechnicalErrorModal } = useReduxModal();
    const { addAlert } = useAlertBanner();
    const { users } = useGetReduxUsers();
    const tenantGroupId = Number(resolvedTenantGroupIdString);
    const formRef = React.useRef<FormikValues>();
    const globalRulesAPI = useGlobalRules({ users });
    const listNamesAPI = useGetListNames();
    const merchantRulesAPI = useMerchantRules({
        merchantId: mid,
        users,
        enabled: overrideLevel === 'merchant'
    });
    const isImpersonationMode = Boolean(landlordId) && landlordId !== tenantKey;

    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });

    const deployedLandlordRulesAPI = useDeployedLandlordRules({
        enabled: overrideLevel === 'tenant'
    });

    const tenantGroupAPI = useGetTenantGroup({
        tenantGroupId: resolvedTenantGroupIdString,
        queryEnabled: overrideLevel === 'tenantGroup'
    });

    const tenantGroupOverridesInheritedAPI = useGetTenantGroupOverridesInherited({
        users,
        tenantGroupId: overrideLevel === 'tenant' ? undefined : resolvedTenantGroupIdString,
        queryEnabled:
            needTenantGroupData &&
            !parametersAPI.isLoading &&
            !parametersAPI.isFetching &&
            !listNamesAPI.isLoading &&
            !listNamesAPI.isFetching,
        parameterData: parametersAPI.data,
        listNameMap: listNamesAPI.data
    });

    const showExecutionOrder = shouldShowExecutionOrder();
    const listAPI = useGetList();

    if (showExecutionOrder) {
        globalRulesAPI.data?.rules.forEach((rule) => {
            if (!rule.priority) {
                rule.priority = 1;
            }
        });
        tenantGroupOverridesInheritedAPI.data?.rules.forEach((rule) => {
            if (!rule.priority) {
                rule.priority = 1;
            }
        });
        deployedLandlordRulesAPI.data?.rules.forEach((rule) => {
            if (!rule.priority) {
                rule.priority = 1;
            }
        });
        merchantRulesAPI.data?.rules.forEach((rule) => {
            if (!rule.priority) {
                rule.priority = 1;
            }
        });
    }

    const referenceRule =
        overrideLevel === 'merchant' || overrideLevel === 'tenantGroup'
            ? globalRulesAPI.data?.rules.find((i) => i.id === ruleId)
            : deployedLandlordRulesAPI.data?.rules.find((i) => i.id === ruleId);

    let overrideRule: any;

    switch (overrideLevel) {
        case 'merchant':
            overrideRule = merchantRulesAPI.data?.rules.find((i) => i.id === ruleId);
            break;
        case 'tenant':
            if (actionType === 'add') {
                if (needTenantGroupData) {
                    overrideRule = tenantGroupOverridesInheritedAPI.data?.rules.find((i) => i.id === ruleId);
                } else {
                    overrideRule = deployedLandlordRulesAPI.data?.rules.find((i) => i.id === ruleId);
                }
            } else if (actionType === 'edit') {
                overrideRule = globalRulesAPI.data?.rules.find((i) => i.id === ruleId);
            }
            break;
        case 'tenantGroup':
            if (actionType === 'add') {
                overrideRule = globalRulesAPI.data?.rules.find((i) => i.id === ruleId);
            } else if (actionType === 'edit') {
                overrideRule = tenantGroupOverridesInheritedAPI.data?.rules.find((i) => i.id === ruleId);
            }
            break;
        default:
            break;
    }

    const globalRule = globalRulesAPI.data?.rules.find((i) => i.id === ruleId);

    const { hasError, errorCmp } = errorController([
        globalRulesAPI.error,
        merchantRulesAPI.error,
        parametersAPI.error,
        listAPI.error,
        deployedLandlordRulesAPI.error,
        tenantGroupOverridesInheritedAPI.error,
        tenantGroupAPI.error
    ]);

    // side-effect for updating multi-level breadcrumbs
    useEffect(() => {
        // update the parent tree breadcrumb.
        // this fixes the issue of missing tenant gruop name in the breadcrumb after a page refresh
        dispatch(
            addAdditionalBreadcrumbParams({
                pathname: `/tenants/tenant-group/${tenantGroupId}`,
                params: {
                    tenantGroupName: tenantGroupInformation?.tenantGroupName || tenantGroupAPI.data?.name
                }
            })
        );
    }, [
        ruleId,
        tenantGroupId,
        dispatch,
        overrideLevel,
        tenantGroupInformation,
        needTenantGroupData,
        tenantGroupAPI.data
    ]);

    useEffect(() => {
        if (overrideRule?.inherit === 'MERCHANT' && actionType === 'add' && overrideLevel === 'merchant') {
            addTechnicalErrorModal(
                '',
                t('Merchant Override Already Exists'),
                () =>
                    history.push(
                        `/overrides/merchant/${mid}/edit-merchant-override/${ruleId}?selectedRowId=${selectedRowId}`
                    ),
                {
                    status: 'warning',
                    content: (
                        <WarningModalContent
                            title={t('This Merchant Override was already added by another user')}
                            subTitle={t('Close the modal to switch to the Edit Override page')}
                        />
                    )
                }
            );
        }
        if (overrideRule?.inherit !== 'MERCHANT' && actionType === 'edit' && overrideLevel === 'merchant') {
            addTechnicalErrorModal(
                '',
                t('Merchant Override Does Not Exist'),
                () =>
                    history.push(
                        `/overrides/merchant/${mid}/add-merchant-override/${ruleId}?selectedRowId=${selectedRowId}`
                    ),

                {
                    status: 'warning',
                    content: (
                        <WarningModalContent
                            title={t('This Merchant Override was deleted by another user and does not exist')}
                            subTitle={t('Close the modal to switch to the Add Override page')}
                        />
                    )
                }
            );
        }

        if (globalRule && globalRule?.isTenantOverride === true && actionType === 'add' && overrideLevel === 'tenant') {
            addTechnicalErrorModal(
                '',
                t('Tenant Override Already Exists'),
                () =>
                    history.push(
                        // eslint-disable-next-line max-len
                        `/global-rules/deployed/landlord/override/edit/${ruleId}?selectedRowId=${selectedRowId}`
                    ),
                {
                    status: 'warning',
                    content: (
                        <WarningModalContent
                            title={t('This Tenant Override was already added by another user')}
                            subTitle={t('Close the modal to switch to the Edit Override page')}
                        />
                    )
                }
            );
        }
        if (
            globalRule &&
            globalRule?.isTenantOverride !== true &&
            actionType === 'edit' &&
            overrideLevel === 'tenant'
        ) {
            addTechnicalErrorModal(
                '',
                t('Tenant Override Does Not Exist'),
                () =>
                    history.push(
                        // eslint-disable-next-line max-len
                        `/global-rules/deployed/landlord/override/add/${ruleId}?selectedRowId=${selectedRowId}`
                    ),
                {
                    status: 'warning',
                    content: (
                        <WarningModalContent
                            title={t('This Tenant Override was deleted by another user and does not exist')}
                            subTitle={t('Close the modal to switch to the Add Override page')}
                        />
                    )
                }
            );
        }
        if (
            overrideRule?.inherit === 'TENANT_GROUP' &&
            actionType === 'add' &&
            overrideLevel === 'tenantGroup' &&
            !(tenantGroupOverridesInheritedAPI.isLoading || tenantGroupOverridesInheritedAPI.isFetching)
        ) {
            addTechnicalErrorModal(
                '',
                t('Tenant Group Override Already Exists'),
                () =>
                    history.push(
                        // eslint-disable-next-line max-len
                        `/tenants/tenant-group/${tenantGroupId}/edit-tenant-group-override/${ruleId}?selectedRowId=${selectedRowId}`
                    ),
                {
                    status: 'warning',
                    content: (
                        <WarningModalContent
                            title={t('This Tenant Group Override was already added by another user')}
                            subTitle={t('Close the modal to switch to the Edit Override page')}
                        />
                    )
                }
            );
        }
        if (
            overrideRule?.inherit === 'ACQUIRER' &&
            actionType === 'edit' &&
            overrideLevel === 'tenantGroup' &&
            (tenantGroupOverridesInheritedAPI.isLoading || tenantGroupOverridesInheritedAPI.isFetching)
        ) {
            addTechnicalErrorModal(
                '',
                t('Tenant Group Override Does Not Exist'),
                () =>
                    history.push(
                        // eslint-disable-next-line max-len
                        `/tenants/tenant-group/${tenantGroupId}/add-tenant-group-override/${ruleId}?selectedRowId=${selectedRowId}`
                    ),
                {
                    status: 'warning',
                    content: (
                        <WarningModalContent
                            title={t('This Tenant Group Override was deleted by another user and does not exist')}
                            subTitle={t('Close the modal to switch to the Add Override page')}
                        />
                    )
                }
            );
        }

        // always scroll to the top
        window.scrollTo(0, 0);
    }, [
        actionType,
        addTechnicalErrorModal,
        history,
        location.pathname,
        overrideRule,
        mid,
        ruleId,
        selectedRowId,
        t,
        overrideLevel,
        globalRule,
        tenantGroupId,
        tenantGroupOverridesInheritedAPI.isLoading,
        tenantGroupOverridesInheritedAPI.isFetching
    ]);

    const handleTrackEventClick = () => {
        let clickIdentifier = '';
        let text = '';
        const isAdvancedRule = Boolean(referenceRule?.expression);

        if (actionType === 'add') {
            if (overrideLevel === 'tenant') {
                clickIdentifier = 'Subjects - Add Override';
                text = 'Add Override';
                if (isImpersonationMode && isAdvancedRule) {
                    clickIdentifier = 'Advanced Tenant Rule - Add Override';
                    text = 'Add Advanced Tenant Rule Override';
                }
            } else if (overrideLevel === 'merchant') {
                clickIdentifier = 'Subjects - Add Advanced Rule Override';
                text = 'Add Advanced Subject-specific Rule Override';
            }
        } else if (actionType === 'edit') {
            if (overrideLevel === 'tenant') {
                clickIdentifier = 'Subjects - Edit Advanced Rule Override';
                text = 'Edit Advanced Subject-specific Rule Override';
                if (isImpersonationMode && isAdvancedRule) {
                    clickIdentifier = 'Advanced Tenant Rule - Edit Override';
                    text = 'Edit Advanced Tenant Rule Override';
                }
            } else if (overrideLevel === 'merchant') {
                if (isAdvancedRule) {
                    clickIdentifier = 'Subjects - Edit Advanced Rule Override';
                    text = 'Edit Advanced Subject-specific Rule Override';
                } else {
                    clickIdentifier = 'Subjects - Edit Override';
                    text = 'Edit Override';
                }
            }
        }

        if (!clickIdentifier || !text) {
            return;
        }
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    const merchantMutation = useUpdateMerchantOverview({
        repoId: templateRepositoryId!,
        revision: merchantRulesAPI.data?.revision!,
        merchantId: mid,
        mutationOptions: {
            onSuccess: () => {
                handleTrackEventClick();
                if (overrideLevel === 'merchant') {
                    addAlert({
                        id: 'merchant-overview-run',
                        status: 'success',
                        content: t('MerchantRuleOverrideSuccess', { ruleId })
                    });
                    history.push(`/overrides/merchant/${mid}?selectedRowId=${selectedRowId}`);
                } else if (overrideLevel === 'tenant') {
                    addAlert({
                        id: 'tenant-overview-run',
                        status: 'success',
                        content: t('TenantRuleOverrideSuccess', { ruleId })
                    });
                    history.push('/global-rules');
                } else {
                    // tenant group override
                    addAlert({
                        id: 'tenantGroup-overview-run',
                        status: 'success',
                        content: t('TenantGroupRuleOverrideSuccess', { ruleId })
                    });
                    history.push(`/tenants/tenant-group/${tenantGroupId}?selectedRowId=${selectedRowId}`);
                }
            },

            onError: () => {
                if (formRef.current) {
                    formRef.current.resetForm();
                }
            }
        },
        actionType,
        overrideLevel,
        tenantGroupId,
        tenantGroupName: tenantGroupInformation?.tenantGroupName || tenantGroupAPI.data?.name
    });

    if (
        listAPI.isFetching ||
        listAPI.isLoading ||
        parametersAPI.isFetching ||
        parametersAPI.isLoading ||
        globalRulesAPI.isLoading ||
        globalRulesAPI.isFetching ||
        (overrideLevel === 'merchant' && (merchantRulesAPI.isLoading || merchantRulesAPI.isFetching)) ||
        (overrideLevel === 'tenant' && (deployedLandlordRulesAPI.isLoading || deployedLandlordRulesAPI.isFetching)) ||
        (needTenantGroupData &&
            (tenantGroupOverridesInheritedAPI.isLoading || tenantGroupOverridesInheritedAPI.isFetching)) ||
        (overrideLevel === 'tenantGroup' && (tenantGroupAPI.isLoading || tenantGroupAPI.isFetching))
    )
        return <RouteLoading />;
    if (hasError) return <>{errorCmp}</>;

    // if the current rule is adhoc rule and createRule featureflag is off,
    // block users from accessing override edit page
    if (!isCreateRuleAllowed && !!referenceRule?.nonTemplated) return <Redirect to='/403' />;
    if ((!overrideRule && actionType === 'edit') || (!referenceRule && actionType === 'add'))
        return <span>{t('This rule does not exist')}</span>;

    let headerText = '';
    if (actionType === 'add') {
        switch (overrideLevel) {
            case 'merchant':
                headerText = t('addOverrideBreadcrumb', { ruleId });
                break;
            case 'tenant':
                headerText = t('addTenantOverrideBreadcrumb', { ruleId });
                break;
            case 'tenantGroup':
                headerText = t('addTenantGroupOverrideBreadcrumb', { ruleId });
                break;
            default:
                break;
        }
    } else if (actionType === 'edit') {
        switch (overrideLevel) {
            case 'merchant':
                headerText = t('editOverrideBreadcrumb', { ruleId });
                break;
            case 'tenant':
                headerText = t('editTenantOverrideBreadcrumb', { ruleId });
                break;
            case 'tenantGroup':
                headerText = t('editTenantGroupOverrideBreadcrumb', { ruleId });
                break;
            default:
                break;
        }
    }

    let rulesType: RulesType;
    let identifier: string = '';
    switch (overrideLevel) {
        case 'merchant':
            rulesType = 'merchantOverrides';
            identifier = mid;
            break;
        case 'tenant':
            rulesType = 'tenantOverrides';
            break;
        default:
            rulesType = 'tenantGroupOverrides';
            identifier = resolvedTenantGroupIdString;
            break;
    }

    if (referenceRule?.expression) {
        return (
            <EditAdvancedRuleForm
                globalRule={referenceRule!}
                parameterData={parametersAPI.data}
                listData={listAPI.data!}
                rulesType={rulesType}
                ruleId={ruleId}
                pageHeader={headerText}
                mutation={merchantMutation}
                selectedRowId={String(selectedRowId) ?? ''}
                overrideRule={overrideRule}
                subjectIdentifier={identifier}
                formRef={formRef}
                actionType={actionType}
            />
        );
    }

    return (
        <EditSimpleRuleForm
            globalRule={referenceRule!}
            parameterData={parametersAPI.data}
            listData={listAPI.data}
            rulesType={rulesType}
            ruleId={ruleId}
            pageHeader={headerText}
            mutation={merchantMutation}
            selectedRowId={String(selectedRowId) ?? ''}
            overrideRule={overrideRule}
            subjectIdentifier={identifier}
            formRef={formRef}
            disabledInputFields={{
                disableDefinitionChange: true,
                disableOverrideChange: true,
                hideOverrideChange: true
            }}
            conclusionFlagListData={conclusionFlagListData}
            actionType={actionType}
            overrideLevel={overrideLevel}
        />
    );
};

export default AddOrEditMerchantOverride;
